import type { Claim, MatterFullState } from '@/types';

/**
 * Client-side health evaluation for claim cards in Stage 3.
 *
 * Six checks per claim — executed after the server has already run its
 * renumber + reference-rewrite pass (``backend/claim_tree.py``), so the
 * text we see here is the *post-adjustment* text. We don't second-guess
 * the server; we just surface remaining issues.
 *
 * Every issue carries an optional ``highlight_text`` — the exact literal
 * substring in ``claim.claim_text`` that the UI should wrap in a yellow
 * ``<mark>`` span. Callers that render the body pass the returned
 * issues through ``highlightSegments()`` below.
 */

export type CheckId = 1 | 2 | 3 | 4 | 5 | 6;

export interface HealthIssue {
  check_id: CheckId;
  check_label: string;
  passed: boolean;
  message: string;
  highlight_text?: string;
  severity: 'warn' | 'advisory';
}

export interface HealthResult {
  claim_id: string;
  status: 'ok' | 'warn';
  issues: HealthIssue[];
  failed_checks: CheckId[];
}

export interface EvaluateOptions {
  idfTexts?: string[];
}

// ─── Shared helpers ──────────────────────────────────────────────────────

const CLAIM_REF_RE = /\bclaim\s+(\d+)\b/gi;
const PLACEHOLDER_RE = /(\[TBD\]|\[PLACEHOLDER\]|\[\.\.\.\]|\[[^\]\n]{0,40}\])/gi;
const SPECIFY_TOKEN_RE = /\u27e6SPECIFY:[^\u27e7]+\u27e7/g;

function plainBody(text: string): string {
  // Strip SPECIFY tokens so checks don't trip over placeholders — those
  // have their own badge + count in the claim header.
  return (text || '').replace(SPECIFY_TOKEN_RE, '');
}

// ─── Check 1 — dependency references resolve ────────────────────────────

function checkDependencyReferences(
  claim: Claim,
  allClaims: Claim[],
): HealthIssue | null {
  const numbers = new Set(allClaims.map((c) => c.claim_number));
  const text = plainBody(claim.claim_text);
  let match: RegExpExecArray | null;
  // Fresh regex per call so lastIndex doesn't leak across claims.
  const re = new RegExp(CLAIM_REF_RE.source, CLAIM_REF_RE.flags);
  while ((match = re.exec(text)) !== null) {
    const n = parseInt(match[1], 10);
    // Self-references are legal ("as described in claim N" inside claim N
    // is unusual but not a structural error) — skip only truly missing N.
    if (!numbers.has(n)) {
      return {
        check_id: 1,
        check_label: 'Dependency reference',
        passed: false,
        message: `Dependency reference to Claim ${n} not found`,
        highlight_text: match[0],
        severity: 'warn',
      };
    }
  }
  return null;
}

// ─── Check 2 — preamble present ─────────────────────────────────────────

function checkPreamble(claim: Claim): HealthIssue | null {
  const text = plainBody(claim.claim_text).trim();
  if (!text) {
    return {
      check_id: 2,
      check_label: 'Preamble',
      passed: false,
      message: 'Claim body is empty',
      severity: 'warn',
    };
  }
  if (claim.claim_type === 'independent') {
    // Strip leading whitespace + claim number prefix like "1." if present.
    const stripped = text.replace(/^\s*\d+\s*\.\s*/, '');
    const ok = /^(A|An|The)\b/.test(stripped);
    if (!ok) {
      const preambleSnippet = stripped.split(/[,.]/, 1)[0].slice(0, 60).trim();
      return {
        check_id: 2,
        check_label: 'Preamble',
        passed: false,
        message: 'Independent claim should start with "A", "An", or "The"',
        highlight_text: preambleSnippet || undefined,
        severity: 'warn',
      };
    }
    return null;
  }
  // Dependent claims: expect "The <thing> of claim N, wherein/further
  // comprising" or "As claimed in claim N". We accept any phrasing that
  // references a parent claim — ``checkDependencyReferences`` catches
  // the harder case where the reference is missing entirely.
  const hasDepPreamble = /\b(of|according to|as claimed in|as in|as recited in)\s+claim\s+\d+/i.test(text);
  if (!hasDepPreamble) {
    const preambleSnippet = text.split(',', 1)[0].slice(0, 80).trim();
    return {
      check_id: 2,
      check_label: 'Preamble',
      passed: false,
      message: 'Dependent claim is missing a parent-claim preamble (e.g. "The system of claim 1, wherein …")',
      highlight_text: preambleSnippet || undefined,
      severity: 'warn',
    };
  }
  return null;
}

// ─── Check 3 — at least one limitation keyword ──────────────────────────

function checkLimitation(claim: Claim): HealthIssue | null {
  const text = plainBody(claim.claim_text).toLowerCase();
  const hasKeyword = /\b(comprising|wherein|configured to|including)\b/.test(text);
  if (!hasKeyword) {
    // Highlight the first sentence so the drafter knows where the body
    // is expected to continue.
    const firstSentence = plainBody(claim.claim_text).split(/[.;]/, 1)[0].slice(0, 120).trim();
    return {
      check_id: 3,
      check_label: 'Limitation keyword',
      passed: false,
      message: 'Claim body should contain "comprising", "wherein", "configured to", or "including"',
      highlight_text: firstSentence || undefined,
      severity: 'warn',
    };
  }
  return null;
}

// ─── Check 4 — no bracket placeholders ──────────────────────────────────

function checkPlaceholder(claim: Claim): HealthIssue | null {
  const text = plainBody(claim.claim_text);
  const match = PLACEHOLDER_RE.exec(text);
  // Reset lastIndex so the shared regex doesn't leak state.
  PLACEHOLDER_RE.lastIndex = 0;
  if (match) {
    return {
      check_id: 4,
      check_label: 'Placeholder text',
      passed: false,
      message: `Placeholder text detected: ${match[0]}`,
      highlight_text: match[0],
      severity: 'warn',
    };
  }
  return null;
}

// ─── Check 4b — unresolved SPECIFY tokens (⟦SPECIFY:…⟧) ─────────────────
//
// SPECIFY tokens are the "manual info still needed" placeholders the
// backend inserts when the IDF doesn't disclose a numeric range,
// component name, etc. They render as clickable amber chips in the
// claim body (see PlaceholderChip in Stage3.tsx) and the claim-navigator
// row already shows their count in an amber pill. We add them to the
// health issues here too so:
//   1. the tree-row health pip flips to amber (matches the pill),
//   2. the hover tooltip lists the specific SPECIFY hints so the
//      drafter knows WHAT manual info is still needed before clicking
//      into the card.
// Inline, the PlaceholderChip already owns the visual — we deliberately
// do NOT return a `highlight_text`, so ClaimBodyRenderer won't add a
// redundant yellow <mark> around the SPECIFY text (the chip already
// highlights it in amber).
function checkSpecifyTokens(claim: Claim): HealthIssue | null {
  const raw = claim.claim_text || '';
  const hits: string[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(SPECIFY_TOKEN_RE.source, 'g');
  while ((m = re.exec(raw)) !== null) {
    hits.push(m[0]);
  }
  const countFromText = hits.length;
  // Trust whichever source reports more — the backend stores a
  // placeholder_count column that's authoritative when text parsing
  // can't match (e.g. UTF-8 mangling through a proxy).
  const count = Math.max(countFromText, claim.placeholder_count || 0);
  if (count === 0) return null;
  const preview = hits.slice(0, 3).map((h) => {
    // "⟦SPECIFY: numerical range for accuracy⟧" → "numerical range for accuracy"
    const inner = h.replace(/^\u27e6SPECIFY:\s*/, '').replace(/\u27e7$/, '').trim();
    return inner ? `• ${inner}` : `• ${h}`;
  });
  const extra = count - preview.length;
  const message = [
    `${count} unresolved SPECIFY placeholder${count === 1 ? '' : 's'} — manual info still needed`,
    ...preview,
    ...(extra > 0 ? [`• … and ${extra} more`] : []),
  ].join('\n');
  return {
    check_id: 4,
    check_label: 'SPECIFY placeholders',
    passed: false,
    message,
    severity: 'warn',
  };
}

// ─── Check 5 — claim number consistent with position ────────────────────

function checkClaimNumber(claim: Claim): HealthIssue | null {
  // "1. A processor comprising …" — the leading integer should match
  // claim.claim_number. Many drafts don't include the number at all,
  // which is fine — we only flag mismatches when a number IS present.
  const m = /^\s*(\d+)\s*\.?\s/.exec(claim.claim_text || '');
  if (m) {
    const printed = parseInt(m[1], 10);
    if (printed !== claim.claim_number) {
      return {
        check_id: 5,
        check_label: 'Claim number',
        passed: false,
        message: `Claim text starts with number ${printed} but this is Claim ${claim.claim_number}`,
        highlight_text: m[0].trim(),
        severity: 'warn',
      };
    }
  }
  return null;
}

// ─── Check 6 — IDF grounding advisory ───────────────────────────────────

const _STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'onto',
  'upon', 'wherein', 'comprising', 'including', 'configured', 'based',
  'claim', 'system', 'method', 'apparatus', 'device', 'module', 'said',
  'thereof', 'therein', 'whereby', 'further', 'first', 'second', 'third',
]);

function _candidateTerms(text: string): string[] {
  // Pull out capitalised multi-word noun-like phrases: they're the most
  // likely to be product-specific identifiers that need to match IDF.
  const matches = text.match(/\b([A-Z][A-Za-z0-9]+(?:\s+[A-Z][A-Za-z0-9]+)+)\b/g);
  if (!matches) return [];
  return Array.from(new Set(
    matches
      .map((m) => m.trim())
      .filter((m) => m.split(/\s+/).every((w) => !_STOPWORDS.has(w.toLowerCase()))),
  ));
}

function checkIdfGrounding(
  claim: Claim,
  options: EvaluateOptions,
): HealthIssue | null {
  const texts = options.idfTexts || [];
  // Spec: if no IDF is indexed, skip silently — no error, no warning.
  if (texts.length === 0) return null;
  const joined = texts.join('\n').toLowerCase();
  if (!joined.trim()) return null;
  const body = plainBody(claim.claim_text);
  const candidates = _candidateTerms(body);
  if (candidates.length === 0) return null;
  const missing = candidates.find((term) => !joined.includes(term.toLowerCase()));
  if (!missing) return null;
  return {
    check_id: 6,
    check_label: 'IDF grounding',
    passed: false,
    message: `Unverified element — "${missing}" not found in any indexed IDF`,
    highlight_text: missing,
    severity: 'advisory',
  };
}

// ─── Public API ─────────────────────────────────────────────────────────

export function evaluateClaimHealth(
  claim: Claim,
  allClaims: Claim[],
  options: EvaluateOptions = {},
): HealthResult {
  const runners: Array<() => HealthIssue | null> = [
    () => checkDependencyReferences(claim, allClaims),
    () => checkPreamble(claim),
    () => checkLimitation(claim),
    () => checkPlaceholder(claim),
    () => checkSpecifyTokens(claim),
    () => checkClaimNumber(claim),
    () => checkIdfGrounding(claim, options),
  ];
  const issues: HealthIssue[] = [];
  for (const runner of runners) {
    const r = runner();
    if (r) issues.push(r);
  }
  return {
    claim_id: claim.id,
    status: issues.length === 0 ? 'ok' : 'warn',
    issues,
    failed_checks: issues.map((i) => i.check_id),
  };
}

export function idfTextsFromState(matterState?: MatterFullState | null): string[] {
  const docs = (matterState as any)?.idf_documents as Array<{ extracted_text?: string }> | undefined;
  if (!docs) return [];
  return docs
    .map((d) => (d?.extracted_text || '').trim())
    .filter((t) => t.length > 0);
}

// ─── Text-highlight utility ────────────────────────────────────────────

export interface HighlightRange {
  start: number;
  end: number;
  message: string;
}

/**
 * Translate a set of ``{ highlight_text, message }`` pairs into
 * non-overlapping character ranges in ``text``. Only the first
 * occurrence of each ``highlight_text`` is matched — highlighting every
 * occurrence would be noisy and redundant given the hover tooltip
 * already lists the full issue.
 *
 * Overlap policy: earlier-found ranges win; later ranges that would
 * overlap are skipped.
 */
export function computeHighlightRanges(
  text: string,
  issues: HealthIssue[],
): HighlightRange[] {
  const ranges: HighlightRange[] = [];
  for (const issue of issues) {
    const needle = issue.highlight_text;
    if (!needle) continue;
    const idx = text.indexOf(needle);
    if (idx < 0) continue;
    const start = idx;
    const end = idx + needle.length;
    const overlaps = ranges.some((r) => !(end <= r.start || start >= r.end));
    if (overlaps) continue;
    ranges.push({ start, end, message: issue.message });
  }
  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}
